import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Switch,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { WordData } from "../types";
import {
  STATUS,
  STATUS_WORDS_MAP,
  STATUS_TAG_COLOR,
} from "../../../utils/consts";
import { post, get } from "../../../utils/http";
import AudioPlayButton from "./AudioPlayButton";

interface WordTableProps {
  data: WordData[];
  sceneId: string;
}

function WordTable({ data: initialData, sceneId }: WordTableProps) {
  const [wordData, setWordData] = useState<WordData[]>(initialData);
  const [wordModalVisible, setWordModalVisible] = useState(false);
  const [wordForm] = Form.useForm();
  const [editingRecord, setEditingRecord] = useState<WordData | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState<WordData | null>(null);
  const [generatingAudioId, setGeneratingAudioId] = useState<string | null>(
    null,
  );

  const fetchWordData = async () => {
    try {
      const response = await get("/api/manage/scene-words", { scene_id: sceneId });
      console.log("🚀 ~ fetchWordData ~ response:", response);
      if (response?.data?.code === 0) {
        setWordData(
          Array.isArray(response.data.data) ? response.data.data : [],
        );
      }
    } catch (error) {
      console.error("🚀 ~ fetchWordData ~ error:", error);
    }
  };

  useEffect(() => {
    setWordData(initialData);
  }, [initialData]);
  const wordColumns: ColumnsType<WordData> = [
    {
      title: "单词",
      dataIndex: "word_text",
      key: "word_text",
    },
    {
      title: "翻译",
      dataIndex: "word_translation",
      key: "word_translation",
    },
    {
      title: "例句",
      dataIndex: "example_sentence",
      key: "example_sentence",
      render: (example_sentence, record) => (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span>{example_sentence || "暂无例句"}</span>
          <Button
            type="link"
            size="small"
            onClick={async () => {
              try {
                const response = await post("/api/manage/generate-word-example", {
                  word_text: record.word_text,
                  word_id: record.id,
                });
                if (response?.data?.code === 0) {
                  message.success("例句生成成功");
                  fetchWordData();
                } else {
                  message.error("例句生成失败");
                }
              } catch (error) {
                console.error("例句生成失败:", error);
                message.error("例句生成失败");
              }
            }}
          >
            AI生成
          </Button>
        </div>
      ),
    },
    {
      title: "音频",
      dataIndex: "voice",
      key: "voice",
      render: (voice, record) =>
        voice ? (
          <AudioPlayButton voiceUrl={voice} recordId={record.id} />
        ) : (
          "暂无音频"
        ),
    },
    {
      title: "是否启用",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Switch
            checked={status === STATUS.INUSE}
            onChange={(checked) => {
              const newStatus = checked ? STATUS.INUSE : STATUS.DISABLE;
              post("/api/manage/word-update", {
                id: record.id,
                status: newStatus,
              })
                .then((res) => {
                  if (res?.data?.code === 0) {
                    message.success("状态更新成功");
                    fetchWordData();
                  } else {
                    message.error("状态更新失败");
                  }
                })
                .catch((error) => {
                  console.error("状态更新失败:", error);
                  message.error("状态更新失败");
                });
            }}
          />
          <Tag color={STATUS_TAG_COLOR[status as STATUS]}>
            {STATUS_WORDS_MAP[status as STATUS]}
          </Tag>
        </span>
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 250,
      render: (_, record) => (
        <span>
          <a style={{ marginRight: 16 }} onClick={() => handleEdit(record)}>
            编辑
          </a>
          <Button
            type="link"
            loading={generatingAudioId === record.id}
            disabled={generatingAudioId !== null}
            onClick={() => handleGenerateAudio(record)}
            style={{ marginRight: 16 }}
          >
            生成音频
          </Button>
          <a onClick={() => handleDelete(record)}>删除</a>
        </span>
      ),
    },
  ];

  const handleAddWord = () => {
    setWordModalVisible(true);
  };

  const handleWordModalOk = async () => {
    try {
      const values = await wordForm.validateFields();
      const apiUrl = editingRecord ? "/api/manage/word-update" : "/api/manage/word-add";
      const requestData = editingRecord
        ? {
            id: editingRecord.id,
            word_text: values.content,
            word_translation: values.translation,
            example_sentence: values.example_sentence,
            status: values.status,
          }
        : {
            scene_id: sceneId,
            word_text: values.content,
            word_translation: values.translation,
            example_sentence: values.example_sentence,
            status: values.status,
          };
      const response = await post(apiUrl, requestData);
      console.log("🚀 ~ handleWordModalOk ~ response:", response);
      if (response?.data?.code === 0) {
        setWordModalVisible(false);
        wordForm.resetFields();
        setEditingRecord(null);
        fetchWordData();
      }
    } catch (error) {
      console.error("🚀 ~ handleWordModalOk ~ error:", error);
    }
  };

  const handleWordModalCancel = () => {
    setWordModalVisible(false);
    wordForm.resetFields();
    setEditingRecord(null);
  };

  const handleEdit = (record: WordData) => {
    setEditingRecord(record);
    wordForm.setFieldsValue({
      content: record.word_text,
      translation: record.word_translation,
      example_sentence: record.example_sentence,
      status: record.status,
    });
    setWordModalVisible(true);
  };

  const handleDelete = (record: WordData) => {
    setDeletingRecord(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRecord) return;
    try {
      const response = await post("/api/manage/word-delete", {
        id: deletingRecord.id,
      });
      console.log("🚀 ~ handleDeleteConfirm ~ response:", response);
      if (response?.data?.code === 0) {
        setDeleteModalVisible(false);
        setDeletingRecord(null);
        fetchWordData();
      }
    } catch (error) {
      console.error("🚀 ~ handleDeleteConfirm ~ error:", error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeletingRecord(null);
  };

  const handleGenerateAudio = async (record: WordData) => {
    if (generatingAudioId !== null) {
      return;
    }

    setGeneratingAudioId(record.id);

    try {
      const response = await post("/api/manage/text-to-audio-and-save", {
        text: record.word_text,
        table_type: "word",
        record_id: record.id,
      });
      console.log("🚀 ~ handleGenerateAudio ~ response:", response);
      if (response?.data?.code === 0) {
        message.success("音频生成成功");
        fetchWordData();
      }
    } catch (error) {
      console.error("🚀 ~ handleGenerateAudio ~ error:", error);
      message.error("音频生成失败");
    } finally {
      setGeneratingAudioId(null);
    }
  };

  return (
    <>
      <Table
        columns={wordColumns}
        dataSource={wordData}
        rowKey="id"
        pagination={false}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6}>
                <Button
                  type="primary"
                  block
                  size="large"
                  onClick={handleAddWord}
                >
                  添加单词
                </Button>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      <Modal
        title={editingRecord ? "编辑单词" : "添加单词"}
        open={wordModalVisible}
        onOk={handleWordModalOk}
        onCancel={handleWordModalCancel}
        width={600}
      >
        <Form form={wordForm} layout="vertical">
          <Form.Item
            label="单词"
            name="content"
            rules={[{ required: true, message: "请输入单词" }]}
          >
            <Input placeholder="请输入单词" />
          </Form.Item>
          <Form.Item
            label="翻译"
            name="translation"
            rules={[{ required: true, message: "请输入翻译" }]}
          >
            <Input placeholder="请输入翻译" />
          </Form.Item>
          <Form.Item label="例句" name="example_sentence">
            <Input placeholder="请输入例句" />
          </Form.Item>
          <Form.Item
            label="是否启用"
            name="status"
            rules={[{ required: true, message: "请选择是否启用" }]}
          >
            <Select placeholder="请选择是否启用">
              <Select.Option value={STATUS.INUSE}>
                {STATUS_WORDS_MAP[STATUS.INUSE]}
              </Select.Option>
              <Select.Option value={STATUS.DISABLE}>
                {STATUS_WORDS_MAP[STATUS.DISABLE]}
              </Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="确认删除"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="确认"
        cancelText="取消"
      >
        <p>确定要删除这个单词吗？</p>
      </Modal>
    </>
  );
}

export default WordTable;
